import React, { useRef } from 'react';
import type { Arrow, Box } from './types';
import { getConnPt, getArrowWaypoints, segCrossing, buildBridgedPath, BRIDGE_R } from './lib/arrowRouting';

// ── Props ─────────────────────────────────────────────────────────────────────

// Per-action argument tuples — see the matching CanvasBoxActionArgs in
// CanvasBox.tsx for why this replaces a plain `...args: number[]` (which let
// e.g. 'delete' incorrectly type-check with extra numeric args tacked on).
export type CanvasArrowActionArgs = {
  hoverEnter: [];
  hoverLeave: [];
  delete: [];
  showContextMenu: [x: number, y: number];
};

export type CanvasArrowAction = keyof CanvasArrowActionArgs;

export interface CanvasArrowProps {
  arrow: Arrow;
  arrowIdx: number;
  allArrows: Arrow[];
  boxes: Box[];
  /** Pre-built id→box lookup, shared across every CanvasArrow instance for this render —
   *  built once by the caller (FlowchartBuilder, via useGanttSchedule's arrowBoxesById)
   *  instead of every single arrow rebuilding its own copy of the same map (O(boxes ×
   *  arrows) per render otherwise, on top of the O(arrows²) crossing-detection cost below). */
  boxesById: Map<number, Box>;

  isHovered: boolean;
  isConnectedToSelection: boolean;
  showNotes: boolean;
  showCriticalPathCanvas: boolean;
  criticalPathArrowIds: Set<number>;
  conflictArrowIds: Set<number>;
  /** True while any box is actively being dragged on the canvas. The crossing/bridge
   *  computation below is O(arrows²) (a nested waypoint-generation + segment-intersection
   *  pass against every other arrow), and every arrow's `boxes` prop changes reference on
   *  every drag frame (any box's x/y moving invalidates the shared geometry fingerprint),
   *  so normally ALL arrows re-run this full pass on every single frame of a drag — very
   *  noticeable on a flowchart with 100+ arrows (e.g. "DR1 DMLT": 128 boxes/172 arrows).
   *  While dragging, skip crossing-detection and draw the plain polyline instead; the
   *  bridge/arc decoration is purely cosmetic, so it snaps back the instant the drag ends. */
  isDraggingActive: boolean;

  // Single stable dispatcher instead of 4 separate handler props (onHoverEnter,
  // onHoverLeave, onDelete, onShowContextMenu) — see FlowchartBuilder.tsx for why:
  // a referentially-stable prop here is what lets React.memo actually skip
  // re-rendering arrows unaffected by e.g. panning or selecting a different box.
  onAction: <A extends CanvasArrowAction>(action: A, arrow: Arrow, ...args: CanvasArrowActionArgs[A]) => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

const CanvasArrow = React.memo(function CanvasArrow({
  arrow: a, arrowIdx, allArrows: arrows, boxes, boxesById,
  isHovered, isConnectedToSelection, showNotes,
  showCriticalPathCanvas, criticalPathArrowIds, conflictArrowIds,
  isDraggingActive,
  onAction,
}: CanvasArrowProps) {
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fb = boxesById.get(a.fromBox);
  const tb = boxesById.get(a.toBox);
  if (!fb || !tb) return null;
  if (!showNotes && (fb.type === 'callout' || tb.type === 'callout')) return null;

  const fp = getConnPt(fb, a.fromSide);
  const tp = getConnPt(tb, a.toSide);
  const mx = (fp.x + tp.x) / 2;
  const my = (fp.y + tp.y) / 2;

  const isCalloutConnection = fb.type === 'callout' || tb.type === 'callout';

  // Compute waypoints and build the path (with optional bridges/arcs at crossings)
  let pathD: string;
  if (isCalloutConnection) {
    pathD = `M ${fp.x} ${fp.y} L ${tp.x} ${tp.y}`;
  } else {
    const wpts = getArrowWaypoints(a, boxes, boxesById);
    // Find crossings with ALL other arrows — the bridge/arc always goes on the horizontal segment.
    // Skipped entirely while a box is being dragged — see isDraggingActive's own comment above.
    const crossingPts: { segIndex: number; t: number; x: number; y: number }[] = [];
    if (!isDraggingActive) {
      for (let j = 0; j < arrows.length; j++) {
        if (j === arrowIdx) continue;
        const other = arrows[j];
        const fb2 = boxesById.get(other.fromBox);
        const tb2 = boxesById.get(other.toBox);
        if (!fb2 || !tb2) continue;
        if (fb2.type === 'callout' || tb2.type === 'callout') continue;
        const owpts = getArrowWaypoints(other, boxes, boxesById);
        for (let si = 0; si < wpts.length - 1; si++) {
          // Bridge/arc only on horizontal segments (|dx| > |dy|)
          const segDx = wpts[si + 1].x - wpts[si].x;
          const segDy = wpts[si + 1].y - wpts[si].y;
          if (Math.abs(segDy) >= Math.abs(segDx)) continue;
          for (let sj = 0; sj < owpts.length - 1; sj++) {
            const cross = segCrossing(
              wpts[si].x, wpts[si].y, wpts[si + 1].x, wpts[si + 1].y,
              owpts[sj].x, owpts[sj].y, owpts[sj + 1].x, owpts[sj + 1].y,
            );
            if (cross) {
              // Skip crossings too close to the endpoints of this segment
              const tooClose = [wpts[si], wpts[si + 1]].some(
                w => Math.sqrt((cross.x - w.x) ** 2 + (cross.y - w.y) ** 2) < BRIDGE_R * 2,
              );
              if (!tooClose) crossingPts.push({ segIndex: si, t: cross.t, x: cross.x, y: cross.y });
            }
          }
        }
      }
    }
    pathD = buildBridgedPath(wpts, crossingPts);
  }

  // Compute the bend/corner position for the delete button
  let bendX: number, bendY: number;
  if (isCalloutConnection) {
    bendX = mx;
    bendY = my;
  } else if (a.fromSide === 'left' || a.fromSide === 'right') {
    if (a.toSide === 'left' || a.toSide === 'right') {
      bendX = (fp.x + tp.x) / 2;
      bendY = (fp.y + tp.y) / 2;
    } else {
      bendX = tp.x;
      bendY = fp.y;
    }
  } else {
    if (a.toSide === 'top' || a.toSide === 'bottom') {
      bendX = (fp.x + tp.x) / 2;
      bendY = (fp.y + tp.y) / 2;
    } else {
      bendX = fp.x;
      bendY = tp.y;
    }
  }

  const isCritical = showCriticalPathCanvas && criticalPathArrowIds.has(a.id);

  return (
    <g data-arrow-el="true">
      {/* Wider invisible stroke for easier hovering — click no longer deletes (too easy to
          hit by accident); only the explicit × button below does that now. Right-click
          still opens the delete confirmation menu, same as the mobile long-press below. */}
      <path
        d={pathD}
        stroke="transparent"
        strokeWidth="20"
        fill="none"
        className="cursor-pointer pointer-events-auto"
        onMouseEnter={() => onAction('hoverEnter', a)}
        onMouseLeave={() => onAction('hoverLeave', a)}
        onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); onAction('showContextMenu', a, e.clientX, e.clientY); }}
        onTouchStart={(e) => {
          // No stopPropagation — the native event must be able to bubble up to the canvas handler
          const touch = e.touches[0];
          const touchX = touch.clientX;
          const touchY = touch.clientY;
          if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
          longPressTimerRef.current = setTimeout(() => {
            longPressTimerRef.current = null;
            onAction('showContextMenu', a, touchX, touchY);
          }, 500);
        }}
        onTouchEnd={() => {
          if (longPressTimerRef.current) { clearTimeout(longPressTimerRef.current); longPressTimerRef.current = null; }
        }}
        onTouchMove={() => {
          if (longPressTimerRef.current) { clearTimeout(longPressTimerRef.current); longPressTimerRef.current = null; }
        }}
      />
      {/* Visible arrow path */}
      <path
        d={pathD}
        stroke={isCalloutConnection ? "#eab308" : isCritical ? "#ef4444" : "#3b82f6"}
        strokeWidth={isConnectedToSelection ? 4 : isCritical ? 2.5 : 2}
        fill="none"
        strokeDasharray={a.dashed ? "5,5" : "none"}
        markerEnd={isCalloutConnection ? "none" : isCritical ? "url(#ah-cp)" : "url(#ah)"}
        className="pointer-events-none"
      />

      {/* Scheduling conflict badge: red ! near arrowhead */}
      {conflictArrowIds.has(a.id) && !isCalloutConnection && (() => {
        const exX = tp.x + (a.toSide === 'left' ? -20 : a.toSide === 'right' ? 20 : 0);
        const exY = tp.y + (a.toSide === 'top' ? -20 : a.toSide === 'bottom' ? 20 : 0);
        return (
          <g className="pointer-events-none">
            <circle cx={exX} cy={exY} r="8" fill="#ef4444" />
            <text x={exX} y={exY} textAnchor="middle" dominantBaseline="middle" fill="white" fontSize="13" fontWeight="bold">!</text>
          </g>
        );
      })()}

      {/* Delete button at bend - only visible on hover */}
      {isHovered && (
        <g onMouseEnter={() => onAction('hoverEnter', a)} onMouseLeave={() => onAction('hoverLeave', a)}>
          <circle cx={bendX} cy={bendY} r="20" fill="transparent" className="cursor-pointer pointer-events-auto" onClick={() => onAction('delete', a)} />
          <circle cx={bendX} cy={bendY} r="10" fill="white" stroke="#ef4444" strokeWidth="2" className="cursor-pointer pointer-events-auto" onClick={() => onAction('delete', a)} />
          <text x={bendX} y={bendY + 1} textAnchor="middle" dominantBaseline="middle" fill="#ef4444" fontSize="14" fontWeight="bold" className="cursor-pointer pointer-events-auto select-none" onClick={() => onAction('delete', a)}>×</text>
        </g>
      )}
    </g>
  );
});

export default CanvasArrow;
